import { useMemo, useState } from "react";
import { toast } from "sonner";
import { navigate } from "wouter/use-browser-location";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export type CleoTurn = {
  id: number;
  question: string;
  response: string;
  topic: string | null;
  sentiment: string | null;
  createdAt: string;
};

export type TimelineMilestone = {
  id: string;
  kind?: string;
  at: string;
  label: string;
  sub: string | null;
  linkA: string;
  proposalId?: number;
  workspacePath?: string;
  publicProposalPath?: string;
  proposalRef?: string | null;
  updateText?: string | null;
  reminderLabel?: string | null;
  salespersonName?: string | null;
  sourceLabel?: string | null;
  interactionSource?: string | null;
  cleoSession?: CleoTurn[];
};

function dayLabel(d: Date) {
  return d.toLocaleDateString("en-SG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function dayKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const kindStyles: Record<string, { border: string; dot: string }> = {
  customer_log: { border: "border-l-emerald-500", dot: "bg-emerald-500" },
  proposal: { border: "border-l-sky-500", dot: "bg-sky-600" },
  cleo: { border: "border-l-violet-500", dot: "bg-violet-600" },
  communication: { border: "border-l-amber-500", dot: "bg-amber-500" },
  default: { border: "border-l-[#1a2744]/40", dot: "bg-[#1a2744]" },
};

function proposalWorkspacePath(m: TimelineMilestone): string {
  return String(m.workspacePath ?? m.linkA ?? "").trim();
}

function proposalPublicPath(m: TimelineMilestone): string {
  return String(m.publicProposalPath ?? "").trim();
}

function ProposalTimelineActions({ milestone }: { milestone: TimelineMilestone }) {
  const workspace = proposalWorkspacePath(milestone);
  const publicPath = proposalPublicPath(milestone);

  return (
    <div className="flex flex-wrap gap-2 px-3 pb-3 pt-1">
      <Button
        type="button"
        size="sm"
        className="h-8 bg-[#1a2744] text-white hover:bg-[#1a2744]/90"
        onClick={() => {
          if (!workspace) {
            toast.error("Proposal workspace unavailable");
            return;
          }
          navigate(workspace);
        }}
      >
        Open Proposal Workspace
      </Button>
      {publicPath ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 border-[#1a2744]/20"
          onClick={() => navigate(publicPath)}
        >
          Open Public Proposal
        </Button>
      ) : null}
    </div>
  );
}

export function CustomerCardTimeline({ milestones }: { milestones: TimelineMilestone[] }) {
  const sorted = useMemo(
    () => [...milestones].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()),
    [milestones]
  );

  const [cleoOpen, setCleoOpen] = useState(false);
  const [cleoPayload, setCleoPayload] = useState<CleoTurn[] | null>(null);

  if (sorted.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-lg">
        No timeline events yet. Save a customer log or create a proposal to see activity here.
      </p>
    );
  }

  let lastDay: string | null = null;

  return (
    <div className="relative max-w-2xl">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#1a2744]/15" aria-hidden />
      <ul className="space-y-0">
        {sorted.map((m) => {
          const d = new Date(m.at);
          const dk = dayKey(d);
          const showDay = lastDay !== dk;
          lastDay = dk;
          const st = kindStyles[m.kind ?? ""] ?? kindStyles.default;
          const isCleo = m.kind === "cleo" && m.cleoSession && m.cleoSession.length > 0;
          const isProposal = m.kind === "proposal";
          const isLinkedCleoLog = Boolean(m.proposalRef && m.updateText);
          return (
            <li key={m.id} className="relative pl-8 pb-6 last:pb-0">
              <span
                className={`absolute left-0 top-1.5 h-3.5 w-3.5 rounded-full border-2 border-white ring-2 ring-[#FAFAF7] ${st.dot}`}
              />
              {showDay ? (
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#1a2744]/50 mb-2">{dayLabel(d)}</p>
              ) : null}
              <div className={`rounded-lg border border-[#1a2744]/10 bg-white/90 pl-3 ${st.border} border-l-4 shadow-sm`}>
                {isLinkedCleoLog ? (
                  <div className="px-3 py-2.5 space-y-1.5">
                    <p className="text-[11px] text-muted-foreground">
                      {d.toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-sm font-semibold text-[#1a2744]">{m.label}</p>
                    <p className="text-xs text-[#1a2744]/80">
                      <span className="font-medium">Proposal:</span>{" "}
                      <button
                        type="button"
                        className="font-mono text-sky-700 underline-offset-2 hover:underline"
                        onClick={() => {
                          const workspace = proposalWorkspacePath(m);
                          if (!workspace) {
                            toast.error("Proposal workspace unavailable");
                            return;
                          }
                          navigate(workspace);
                        }}
                      >
                        [{m.proposalRef}]
                      </button>
                    </p>
                    <div className="text-xs text-[#1a2744]/80">
                      <p className="font-medium">Update:</p>
                      <p className="whitespace-pre-wrap">{m.updateText}</p>
                    </div>
                    {m.reminderLabel ? (
                      <p className="text-xs text-[#1a2744]/80">
                        <span className="font-medium">Reminder:</span> {m.reminderLabel}
                      </p>
                    ) : null}
                    {m.salespersonName ? (
                      <p className="text-[11px] text-muted-foreground">By {m.salespersonName}</p>
                    ) : null}
                    {m.sourceLabel ? (
                      <p className="text-[10px] font-medium text-[#1a2744]/60">Source: {m.sourceLabel}</p>
                    ) : null}
                  </div>
                ) : isProposal ? (
                  <>
                    <div className="px-3 py-2.5">
                      <p className="text-[11px] text-muted-foreground">
                        {d.toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                      <p className="text-sm font-semibold text-[#1a2744] mt-0.5">{m.label}</p>
                      {m.sub ? <p className="text-xs text-[#1a2744]/80 mt-1 whitespace-pre-wrap">{m.sub}</p> : null}
                    </div>
                    <ProposalTimelineActions milestone={m} />
                  </>
                ) : (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2.5"
                    onClick={() => {
                      if (isCleo) {
                        setCleoPayload(m.cleoSession!);
                        setCleoOpen(true);
                      } else {
                        const link = m.linkA?.trim();
                        if (!link) {
                          toast.error("Link unavailable");
                          return;
                        }
                        navigate(link);
                      }
                    }}
                  >
                    <p className="text-[11px] text-muted-foreground">
                      {d.toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                    <p className="text-sm font-semibold text-[#1a2744] mt-0.5">{m.label}</p>
                    {m.sub ? <p className="text-xs text-[#1a2744]/80 mt-1 whitespace-pre-wrap">{m.sub}</p> : null}
                    <p className="text-[10px] font-medium text-sky-700 mt-1.5">
                      {isCleo ? "View Cleo conversation →" : "Open →"}
                    </p>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <Dialog open={cleoOpen} onOpenChange={setCleoOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cleo conversation (customer)</DialogTitle>
            <DialogDescription>Questions and answers from the public proposal page (Link B).</DialogDescription>
          </DialogHeader>
          {cleoPayload && cleoPayload.length > 0 ? (
            <div className="space-y-4 text-sm">
              {cleoPayload.map((t) => (
                <div key={t.id} className="rounded-md border border-[#1a2744]/10 p-3 space-y-2 bg-[#FAFAF7]/80">
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(t.createdAt).toLocaleString("en-SG")}
                    {t.topic ? ` · ${t.topic}` : ""}
                  </p>
                  <p className="font-medium text-[#1a2744]">Q: {t.question}</p>
                  <p className="text-[#1a2744]/90 whitespace-pre-wrap">A: {t.response}</p>
                </div>
              ))}
            </div>
          ) : null}
          <Button type="button" variant="outline" onClick={() => setCleoOpen(false)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
